import { call, put } from 'redux-saga/effects';
import { fetchDossiers, toggleWatchedState, SIGToggleWatchedState } from 'services';
import {
  addToDossier,
  createDossier,
  deleteDossiers,
  fetchDossier,
  fetchDossierItems,
} from 'services/dossier/dossierService';
import { putNotifySuccess, putNotifyError } from 'shared/functions';
import { watchlistItemTypes } from 'shared/constants/watchlistItemTypes';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';

import { toggleMode } from 'shared/constants/toggleMode';
import { ratingTypes } from 'shared/constants/ratingTypes';
import {
  portfolioErrorMessages,
  PortfolioErrorType,
} from 'shared/constants/portfolio/portfolioErrorTypes';
import { titleByRatingType } from 'shared/constants/titleByRatingType';

import { addNotificationAction } from 'store/notifications/notificationsActions';
import {
  GET_DOSSIERS_SUCCESS,
  GET_DOSSIERS_ERROR,
  CREATE_DOSSIER_SUCCESS,
  CREATE_DOSSIER_ERROR,
  DELETE_DOSSIER_SUCCESS,
  DELETE_DOSSIER_ERROR,
  refreshDossiersAction,
  GET_DOSSIER_SUCCESS,
  GET_DOSSIER_ERROR,
  GET_DOSSIER_ITEMS_SUCCESS,
  GET_DOSSIER_ITEMS_ERROR,
  TOGGLE_DOSSIER_WATCHED_STATE_FINISHED,
} from './dossierActions';

const getCallMethodsByRatingType = ratingType => {
  switch (ratingType) {
    case ratingTypes.CREDIT_RATING:
      return toggleWatchedState;
    case ratingTypes.CREDIT_REVIEW:
      return SIGToggleWatchedState;
    default:
      return undefined;
  }
};

export function* fetchDossierSaga({ payload }) {
  try {
    const response = yield call(fetchDossier, payload);
    yield put({ type: GET_DOSSIER_SUCCESS, payload: response });
  } catch (response) {
    yield put({ type: GET_DOSSIER_ERROR, payload: response });
    throw new Error(response);
  }
}

export function* fetchDossierItemsSaga({ payload }) {
  try {
    const response = yield call(fetchDossierItems, payload);
    const { itemType, ratingType } = payload;

    yield put({
      type: GET_DOSSIER_ITEMS_SUCCESS,
      payload: { ...response, itemType, ratingType },
    });
  } catch (response) {
    yield put({ type: GET_DOSSIER_ITEMS_ERROR, payload: response });
    yield put(
      addNotificationAction({ message: 'Failed to load portfolio items.', variant: 'error' }),
    );
  }
}

export function* fetchDossiersSaga({ payload }) {
  const { ratingType } = payload;
  try {
    const response = yield call(fetchDossiers, payload);
    yield put({ type: GET_DOSSIERS_SUCCESS, payload: { response, ratingType } });
  } catch (response) {
    yield put({ type: GET_DOSSIERS_ERROR, payload: { response, ratingType } });
    yield putNotifyError(`Failed to get ${titleByRatingType[ratingType]} portfolios.`);
  }
}

export function* createDossierSaga({ payload }) {
  const { ratingType, itemsData, selectAll, sourceDossierId } = payload;
  const addElementsToDossier = itemsData ? itemsData.count !== 0 : selectAll && sourceDossierId;
  try {
    const responseDossier = yield call(createDossier, payload);

    if (responseDossier.error) {
      throw responseDossier;
    }

    if (addElementsToDossier) {
      yield call(addToDossier, {
        itemsData,
        dossierId: responseDossier.data.id,
        ratingType,
        selectAll,
        sourceDossierId,
      });
    }

    yield put({ type: CREATE_DOSSIER_SUCCESS, payload: responseDossier });

    yield putNotifySuccess(
      addElementsToDossier
        ? `Portfolio was successfully created and elements added.`
        : `Portfolio was successfully ${payload.dossierId ? 'edited' : 'created'}.`,
    );
    yield put(refreshDossiersAction({ reason: refreshReasons.CREATED, ratingType }));
  } catch (error) {
    const errorType =
      error.status === 409 ? PortfolioErrorType.CREATE_NAME_CONFLICT : PortfolioErrorType.UNKNOWN;
    const errorMessage = portfolioErrorMessages[errorType];

    yield put({ type: CREATE_DOSSIER_ERROR, payload: { errorType, errorMessage } });
    yield putNotifyError(
      addElementsToDossier ? `Failed to create portfolio or add elements to it.` : errorMessage,
    );
  }
}

export function* deleteDossiersSaga({ payload }) {
  try {
    const { ratingType } = payload;
    const response = yield call(deleteDossiers, payload);
    yield put({ type: DELETE_DOSSIER_SUCCESS, payload: response });
    yield putNotifySuccess('Portfolio(s) deleted successfully');
    yield put(refreshDossiersAction({ reason: refreshReasons.DELETED, ratingType }));
  } catch (response) {
    yield put({ type: DELETE_DOSSIER_ERROR, payload: response });
    yield putNotifyError('Failed to delete portfolio(s)');
    throw new Error(response);
  }
}

export function* toggleDossierWatchedStateSaga({ payload }) {
  try {
    const { ratingType } = payload;

    yield call(getCallMethodsByRatingType(ratingType), {
      ...payload,
      itemType: watchlistItemTypes.DOSSIER,
    });

    yield putNotifySuccess(
      `Portfolio was ${payload.mode === toggleMode.ON ? 'added to' : 'removed from'} watchlist.`,
    );
    yield put(
      refreshDossiersAction({ reason: refreshReasons.WATCHLIST_STATE_CHANGED, ratingType }),
    );
  } catch (response) {
    yield putNotifyError(
      `Failed to ${
        payload.mode === toggleMode.ON ? 'add portfolio to' : 'remove portfolio from'
      } watchlist.`,
    );
    throw new Error(response);
  } finally {
    yield put({ type: TOGGLE_DOSSIER_WATCHED_STATE_FINISHED, payload });
  }
}
